import {
  ArrowsHorizontalIcon,
  CircleIcon,
  PawPrintIcon,
  SpeakerSimpleSlashIcon,
  StairsIcon,
  WheelchairIcon,
  type Icon,
  type IconProps,
} from "@phosphor-icons/react";
import { forwardRef, useEffect, useState } from "react";

/**
 * API names that are not Phosphor's own. The first "What matters to you" groups
 * were saved with `paw`, but Phosphor only has `paw-print`.
 */
const ICON_ALIASES: Record<string, string> = {
  paw: "paw-print",
};

/** `speaker-simple-slash`, `SpeakerSimpleSlash` and ` Speaker Simple Slash ` all match. */
const normalise = (iconName: string) =>
  iconName.toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * The icons the groups launched with, in the page's own bundle: they render on
 * the first paint, and on the server. Every other name is fetched when asked
 * for — see `ICON_LOADERS`.
 */
const BUNDLED_ICONS: Record<string, Icon> = {
  [normalise("stairs")]: StairsIcon,
  [normalise("wheelchair")]: WheelchairIcon,
  [normalise("arrows-horizontal")]: ArrowsHorizontalIcon,
  [normalise("paw-print")]: PawPrintIcon,
  [normalise("speaker-simple-slash")]: SpeakerSimpleSlashIcon,
};

/**
 * One lazy import per Phosphor icon, so an admin can name any of them for a
 * group without a release.
 *
 * Not `import * as PhosphorIcons`: the set is ~12MB of path data across six
 * weights (about 1MB gzipped), and a namespace lookup would ship all of it.
 * This way each icon is its own ~1KB chunk, fetched only when a group asks for
 * it.
 *
 * The path is relative because `import.meta.glob` takes no bare package
 * specifiers, and `exhaustive` because it skips `node_modules` otherwise. If
 * the package ever stops being hoisted to the workspace root this matches
 * nothing and every unlisted icon falls back to the circle — the test beside
 * this file fails when that happens.
 */
const ICON_MODULES = import.meta.glob<Record<string, Icon | undefined>>(
  "../../../../../../node_modules/@phosphor-icons/react/dist/csr/*.es.js",
  { exhaustive: true },
);

/** Normalised name → how to fetch that icon, for every icon in the set. */
const ICON_LOADERS = new Map(
  Object.entries(ICON_MODULES).map(([path, load]) => {
    // ".../csr/SpeakerSimpleSlash.es.js" exports `SpeakerSimpleSlashIcon`.
    const exportName = path.slice(path.lastIndexOf("/") + 1, -".es.js".length);
    return [
      normalise(exportName),
      () => load().then((module) => module[`${exportName}Icon`] ?? CircleIcon),
    ] as const;
  }),
);

/** Icons already fetched, so a row that remounts draws its icon straight away. */
const fetchedIcons = new Map<string, Icon>();
/** In-flight fetches, so two rows naming the same icon share one request. */
const pendingIcons = new Map<string, Promise<Icon>>();
/** One wrapper per name: a new component each render would remount the row's icon. */
const lazyIcons = new Map<string, Icon>();

const fetchIcon = (key: string, load: () => Promise<Icon>) => {
  let pending = pendingIcons.get(key);
  if (!pending) {
    pending = load()
      // A failed chunk request must not leave the row without a glyph.
      .catch(() => CircleIcon)
      .then((icon) => {
        fetchedIcons.set(key, icon);
        return icon;
      });
    pendingIcons.set(key, pending);
  }
  return pending;
};

const createLazyIcon = (key: string, load: () => Promise<Icon>): Icon => {
  const LazyIcon = forwardRef<SVGSVGElement, IconProps>((props, ref) => {
    const [FetchedIcon, setFetchedIcon] = useState<Icon | undefined>(() =>
      fetchedIcons.get(key),
    );

    useEffect(() => {
      if (FetchedIcon) return;
      let cancelled = false;
      void fetchIcon(key, load).then((icon) => {
        if (!cancelled) setFetchedIcon(() => icon);
      });
      return () => {
        cancelled = true;
      };
    }, [FetchedIcon]);

    if (FetchedIcon) return <FetchedIcon ref={ref} {...props} />;

    // An empty box the icon's size while it loads, so the label beside it does
    // not shift when the glyph arrives. Empty rather than the fallback circle:
    // a circle that turns into a lamp reads as a glitch.
    const size = props.size ?? "1em";
    return (
      <span
        aria-hidden="true"
        className={props.className}
        style={{ display: "inline-block", width: size, height: size }}
      />
    );
  });
  LazyIcon.displayName = `LazyAccessibilityIcon(${key})`;
  return LazyIcon;
};

/**
 * Resolves the kebab-case `icon` names returned by `/accessibility/load`
 * (`mattersToYou[].icon`) to Phosphor components. The name is whatever an admin
 * typed for the group, so any icon in the set works.
 *
 * Kept in step with `apps/native/src/onboarding/utils/accessibilityIcons.ts`;
 * both read the same field of the same response.
 *
 * Falls back to a neutral circle so a group saved with a misspelt icon still
 * renders a row instead of crashing.
 */
export const resolveAccessibilityIcon = (iconName?: string): Icon => {
  const name = iconName?.trim();
  if (!name) return CircleIcon;

  const key = normalise(ICON_ALIASES[name.toLowerCase()] ?? name);
  const bundled = BUNDLED_ICONS[key];
  if (bundled) return bundled;

  const load = ICON_LOADERS.get(key);
  if (!load) return CircleIcon;

  let lazyIcon = lazyIcons.get(key);
  if (!lazyIcon) {
    lazyIcon = createLazyIcon(key, load);
    lazyIcons.set(key, lazyIcon);
  }
  return lazyIcon;
};
